/* =========================================================================
 *  Force Shipping Protection (app toggle) ON + keep it on after cart changes
 *  Store: zendralight  |  Theme: Horizon
 *
 *  The shipping-protection app ships its toggle OFF by default AND drops the
 *  protection from the cart on some cart mutations (e.g. decreasing an item's
 *  quantity), leaving the toggle visually ON but the protection gone.
 *
 *  This script:
 *   1. Turns the toggle ON when the cart/drawer first shows it OFF.
 *   2. After ANY cart write (add / change / update), checks the cart and, if
 *      the toggle is ON but the protection line was dropped, restores it.
 *   3. Respects a REAL manual opt-out: if the customer clicks the toggle OFF
 *      themselves, it stops forcing. (Our own programmatic clicks are ignored
 *      via event.isTrusted, so they never count as an opt-out.)
 *  =======================================================================*/
(function () {
  'use strict';

  var CONFIG = {
    // Visible text next to the app's toggle (lowercase, partial match).
    labelText: 'shipping protection',

    // OPTIONAL but recommended for reliability: the protection VARIANT id.
    // How to find it: add protection to the cart, open  yourstore.com/cart.js
    // in the browser, find the protection item, copy its "variant_id".
    // With this set, restores are done by a direct, reliable add.
    // Leave 0 to restore by "bouncing" the app's own toggle instead.
    protectionVariantId: 0,

    // How the protection line is recognised inside /cart.js.
    protectionMatch: /protection/i,

    debug: false
  };

  var optedOut = false;   // true only when the REAL user turns the toggle off
  var suppress = false;   // guard so our own restore writes don't re-trigger
  var clickCool = 0;      // small cooldown to avoid double-clicking

  function log() {
    if (CONFIG.debug) console.log.apply(console, ['[SP-Force]'].concat([].slice.call(arguments)));
  }

  /* -------------------- toggle helpers -------------------- */
  function findToggle() {
    var nodes = document.querySelectorAll('label,span,div,p,strong,h2,h3,h4');
    var labelEl = null;
    for (var i = 0; i < nodes.length; i++) {
      var txt = (nodes[i].textContent || '').trim().toLowerCase();
      if (txt && txt.length < 80 && txt.indexOf(CONFIG.labelText) !== -1) { labelEl = nodes[i]; break; }
    }
    if (!labelEl) return null;
    var c = labelEl;
    for (var d = 0; d < 5 && c; d++) {
      var ctrl = c.querySelector('input[type="checkbox"],[role="switch"],[aria-checked],button[aria-pressed]');
      if (ctrl) return ctrl;
      c = c.parentElement;
    }
    return null;
  }

  function isOn(ctrl) {
    if (!ctrl) return false;
    if (ctrl.tagName === 'INPUT' && ctrl.type === 'checkbox') return ctrl.checked;
    if (ctrl.hasAttribute('aria-checked')) return ctrl.getAttribute('aria-checked') === 'true';
    if (ctrl.hasAttribute('aria-pressed')) return ctrl.getAttribute('aria-pressed') === 'true';
    return /(active|checked|enabled|--on|is-on)/i.test(ctrl.className);
  }

  function safeClick(ctrl) {
    if (!ctrl) return;
    if (Date.now() - clickCool < 300) return;
    clickCool = Date.now();
    log('click toggle');
    ctrl.click();
  }

  /* -------------------- cart helpers -------------------- */
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  function protectionInCart(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (it) {
      return CONFIG.protectionMatch.test(it.title || '') ||
             CONFIG.protectionMatch.test(it.product_title || '') ||
             CONFIG.protectionMatch.test(it.handle || '');
    });
  }
  function addVariant(id) {
    suppress = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    }).then(function (r) { return r.json(); })
      .catch(function () {})
      .then(function () { setTimeout(function () { suppress = false; }, 600); });
  }

  /* -------------------- light pass (drawer open / first render) -------------------- */
  function ensureToggleOn() {
    if (optedOut || suppress) return;
    var ctrl = findToggle();
    if (ctrl && !isOn(ctrl)) safeClick(ctrl);
  }

  /* -------------------- full pass (after a cart write) -------------------- */
  function reconcileAfterWrite() {
    if (optedOut || suppress) return;
    var ctrl = findToggle();
    if (!ctrl) return;

    // Toggle OFF -> turn it on (app adds protection).
    if (!isOn(ctrl)) { safeClick(ctrl); return; }

    // Toggle ON -> verify protection is actually still in the cart.
    getCart().then(function (cart) {
      if (protectionInCart(cart)) return;            // all good
      log('toggle ON but protection dropped -> restoring');

      if (CONFIG.protectionVariantId) {
        addVariant(CONFIG.protectionVariantId);      // reliable direct re-add
      } else {
        // Fallback: bounce the app's own toggle so the app re-adds it.
        suppress = true;
        safeClick(ctrl);                             // off
        setTimeout(function () {
          var c = findToggle();
          if (c && !isOn(c)) safeClick(c);           // on
          setTimeout(function () { suppress = false; }, 600);
        }, 450);
      }
    });
  }

  /* -------------------- detect a genuine manual opt-out -------------------- */
  document.addEventListener('click', function (e) {
    if (!e.isTrusted) return;                        // ignore our own clicks
    var ctrl = findToggle();
    if (!ctrl) return;
    var hitToggle = ctrl === e.target || ctrl.contains(e.target) ||
                    (e.target.closest && e.target.closest('label') &&
                     e.target.closest('label').contains(ctrl));
    if (!hitToggle) return;
    setTimeout(function () {                          // let the app flip state first
      var c = findToggle();
      optedOut = c ? !isOn(c) : false;
      log('manual click — optedOut =', optedOut);
    }, 200);
  }, true);

  /* -------------------- intercept cart writes -------------------- */
  var _fetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isWrite = /\/cart\/(add|change|update|clear)/.test(url);
    var p = _fetch.apply(this, arguments);
    if (isWrite && !suppress) {
      p.then(function () { setTimeout(reconcileAfterWrite, 350); }).catch(function () {});
    }
    return p;
  };
  // Some themes still use XHR for cart writes — cover that too.
  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', function () {
      if (/\/cart\/(add|change|update|clear)/.test(url) && !suppress) {
        setTimeout(reconcileAfterWrite, 350);
      }
    });
    return _open.apply(this, arguments);
  };

  /* -------------------- init + watch drawer -------------------- */
  function start() {
    ensureToggleOn();
    var obs = new MutationObserver(function () {
      clearTimeout(start._t);
      start._t = setTimeout(ensureToggleOn, 200);
    });
    obs.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'aria-checked', 'aria-pressed', 'open', 'aria-hidden']
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();